import { useState, useRef } from "react";
import { useMutation } from "@tanstack/react-query";
import { Upload, FileText, AlertCircle, Check, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useTRPC } from "@/trpc";
import type { ScheduleEntryValue } from "@/components/schedule-input/schedule-input";
import { format, parseISO } from "date-fns";

interface ImportScheduleDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onImport: (schedules: ScheduleEntryValue[]) => void;
}

interface ParsedSchedule {
  startDate: string | null;
  endDate: string | null;
  startTime: string | null;
  endTime: string | null;
  recurrence: {
    enabled: boolean;
    freq: "DAILY" | "WEEKLY";
    interval: number;
    byDay: string[];
    endType: "count" | "until";
    count: number;
    until: string | null;
  };
  isOnCall: boolean;
}

interface ParseResult {
  schedules: ParsedSchedule[];
  warnings: string[];
  detectedTimezone: string | null;
}

const ACCEPTED_FILE_TYPES = [
  "application/pdf",
  "image/png",
  "image/jpeg",
  "image/heic",
  "image/webp",
].join(",");

export function ImportScheduleDialog({
  isOpen,
  onOpenChange,
  onImport,
}: ImportScheduleDialogProps) {
  const trpc = useTRPC();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [parseResult, setParseResult] = useState<ParseResult | null>(null);
  const [dragActive, setDragActive] = useState(false);

  const parseMutation = useMutation(
    trpc.jobOffer.parseScheduleFile.mutationOptions({
      onSuccess: (data) => {
        setParseResult(data);
      },
    }),
  );

  const handleFileSelect = (file: File) => {
    setSelectedFile(file);
    setParseResult(null);

    // Create FormData and send to API
    const formData = new FormData();
    formData.append("file", file);

    parseMutation.mutate(formData);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleImport = () => {
    if (parseResult?.schedules) {
      onImport(parseResult.schedules as ScheduleEntryValue[]);
      handleClose();
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setParseResult(null);
    parseMutation.reset();
    onOpenChange(false);
  };

  const formatSchedulePreview = (schedule: ParsedSchedule) => {
    if (!schedule.startDate || !schedule.startTime || !schedule.endTime) {
      return "Invalid schedule";
    }

    const date = format(parseISO(schedule.startDate), "MMM d, yyyy");
    const startTime = schedule.startTime;
    const endTime = schedule.endTime;
    const onCallLabel = schedule.isOnCall ? " (On-Call)" : "";

    return `${date}: ${startTime} - ${endTime}${onCallLabel}`;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="w-full max-w-lg">
        <DialogHeader>
          <DialogTitle>Import Schedule from File</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* File Upload Area */}
          <div
            className={`
              relative border-2 border-dashed rounded-lg p-8 text-center transition-colors
              ${dragActive ? "border-blue-500 bg-blue-50" : "border-gray-300"}
              ${selectedFile ? "border-green-500 bg-green-50" : ""}
            `}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED_FILE_TYPES}
              onChange={handleInputChange}
              className="hidden"
            />

            {selectedFile ? (
              <div className="flex items-center justify-center gap-3">
                <FileText className="h-8 w-8 text-green-600" />
                <div className="text-left">
                  <p className="font-medium text-gray-900">
                    {selectedFile.name}
                  </p>
                  <p className="text-sm text-gray-500">
                    {(selectedFile.size / 1024).toFixed(1)} KB
                  </p>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="ml-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedFile(null);
                    setParseResult(null);
                    parseMutation.reset();
                  }}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <div
                className="cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="h-10 w-10 mx-auto text-gray-400 mb-3" />
                <p className="text-gray-600 mb-1">
                  Drag and drop a file here, or click to browse
                </p>
                <p className="text-sm text-gray-400">
                  Supports PDF and image files (PNG, JPEG, HEIC, WebP)
                </p>
              </div>
            )}
          </div>

          {/* Loading State */}
          {parseMutation.isPending && (
            <div className="flex items-center justify-center gap-2 py-4">
              <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
              <span className="text-gray-600">Analyzing schedule...</span>
            </div>
          )}

          {/* Error State */}
          {parseMutation.isError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {parseMutation.error?.message ||
                  "Failed to parse the schedule file. Please try again."}
              </AlertDescription>
            </Alert>
          )}

          {/* Parse Results */}
          {parseResult && (
            <div className="space-y-4">
              {/* Detected Timezone */}
              {parseResult.detectedTimezone && (
                <Alert>
                  <AlertDescription>
                    Detected timezone: {parseResult.detectedTimezone}
                  </AlertDescription>
                </Alert>
              )}

              {/* Warnings */}
              {parseResult.warnings && parseResult.warnings.length > 0 && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    <p className="font-medium mb-1">
                      Some items could not be fully parsed:
                    </p>
                    <ul className="list-disc list-inside text-sm">
                      {parseResult.warnings.map((warning, i) => (
                        <li key={i}>{warning}</li>
                      ))}
                    </ul>
                  </AlertDescription>
                </Alert>
              )}

              {/* Parsed Schedules Preview */}
              {parseResult.schedules.length > 0 ? (
                <div className="border rounded-lg overflow-hidden">
                  <div className="bg-gray-50 px-4 py-2 border-b">
                    <h4 className="font-medium text-sm text-gray-700">
                      Found {parseResult.schedules.length} schedule
                      {parseResult.schedules.length !== 1 ? "s" : ""}
                    </h4>
                  </div>
                  <div className="max-h-60 overflow-y-auto">
                    <ul className="divide-y">
                      {parseResult.schedules.map((schedule, index) => (
                        <li
                          key={index}
                          className="px-4 py-2 text-sm flex items-center gap-2"
                        >
                          <Check className="h-4 w-4 text-green-600 flex-shrink-0" />
                          <span>{formatSchedulePreview(schedule)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ) : (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    No schedules could be extracted from this file. Please check
                    the file format and try again.
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:gap-2">
          <Button type="button" variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleImport}
            disabled={
              !parseResult ||
              parseResult.schedules.length === 0 ||
              parseMutation.isPending
            }
          >
            Import {parseResult?.schedules.length || 0} Schedule
            {parseResult?.schedules.length !== 1 ? "s" : ""}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
